export type ParsedAgentSessionKey = {
  agentId: string;
  rest: string;
  branch?: string;
  repo?: string;
  worktree?: string;
};

// Destination segments recognised after the legacy body. Each appears at
// most once. `branch:` is a single segment; `repo:` and `wt:` may contain
// colons (URLs and absolute paths) so we consume the rest of the string.
const DEST_KEYS = ["branch", "repo", "wt"] as const;
type DestKey = (typeof DEST_KEYS)[number];

function findDestMarker(s: string): { key: DestKey; index: number } | null {
  // Look for the LAST occurrence of a known marker so that an embedded
  // marker inside a `repo:` URL doesn't get mis-split. The marker may
  // appear at the start of `s` without a leading `:` (e.g. when the
  // destination is the first thing after `agent:<id>:`).
  let latest = -1;
  let latestKey: DestKey | null = null;
  for (const key of DEST_KEYS) {
    const withColon = s.lastIndexOf(`:${key}:`);
    const atStart = key === "branch" && s.startsWith(`${key}:`) ? 0 : -1;
    const idx = Math.max(withColon, atStart);
    if (idx > latest) {
      latest = idx;
      latestKey = key;
    }
  }
  if (latest < 0 || latestKey === null) return null;
  return { key: latestKey, index: latest };
}

export function parseAgentSessionKey(
  sessionKey: string | undefined | null,
): ParsedAgentSessionKey | null {
  const raw = (sessionKey ?? "").trim();
  if (!raw) {
    return null;
  }
  if (!raw.startsWith("agent:")) {
    return null;
  }

  // Peel off `agent:<id>`. The legacy body that follows may itself contain
  // colons (e.g. `acp:runs:1` or a path-like session id), so we never split
  // on `:` globally. Instead we walk destination segments from the right.
  const firstColon = raw.indexOf(":", "agent:".length);
  if (firstColon < 0) {
    return null;
  }
  const agentId = raw.slice("agent:".length, firstColon).trim();
  if (!agentId) {
    return null;
  }
  let body = raw.slice(firstColon + 1);
  if (!body) {
    return null;
  }

  // Pop destination segments from the right. Each marker is consumed with
  // a value that extends up to the next marker or end of string. A marker
  // with no following value is rejected (i.e. `agent:m:r:branch` → null).
  // `wt` is the internal-only key; the public ParsedAgentSessionKey uses
  // `worktree` (see the destructuring translation at the end of the fn).
  const destination: { branch?: string; repo?: string; wt?: string } = {};
  for (let guard = 0; guard < 8; guard++) {
    const marker = findDestMarker(body);
    if (!marker) break;
    // `marker.index` points to the `:` before the key (or 0 when the key
    // is at the very start). The key body starts after the key characters.
    const hasLeadingColon = marker.index > 0;
    const keyStart = hasLeadingColon ? marker.index + 1 : 0;
    const valueStart = keyStart + marker.key.length + 1; // past `key:`
    const after = body.slice(valueStart);
    const nextMarker = findDestMarker(after);
    const valueEnd = nextMarker ? nextMarker.index : after.length;
    const value = after.slice(0, valueEnd).trim();
    if (!value) return null;
    if (destination[marker.key] !== undefined) return null; // duplicate
    (destination as Record<string, string>)[marker.key] = value;
    // Strip the consumed segment: if there was a leading `:`, remove it
    // too so the boundary between rest and the next segment is clean.
    body = hasLeadingColon ? body.slice(0, marker.index) : body.slice(keyStart);
  }

  // The remaining body is the legacy session body. It must be non-empty.
  if (!body.trim()) {
    return null;
  }

  // Translate the internal `wt` key into the public `worktree` field.
  const { wt, ...rest } = destination;
  return { agentId, rest: body, ...rest, ...(wt ? { worktree: wt } : {}) };
}

export function isSubagentSessionKey(sessionKey: string | undefined | null): boolean {
  const raw = (sessionKey ?? "").trim();
  if (!raw) {
    return false;
  }
  if (raw.toLowerCase().startsWith("subagent:")) {
    return true;
  }
  const parsed = parseAgentSessionKey(raw);
  return Boolean((parsed?.rest ?? "").toLowerCase().startsWith("subagent:"));
}

export function isAcpSessionKey(sessionKey: string | undefined | null): boolean {
  const raw = (sessionKey ?? "").trim();
  if (!raw) {
    return false;
  }
  const normalized = raw.toLowerCase();
  if (normalized.startsWith("acp:")) {
    return true;
  }
  const parsed = parseAgentSessionKey(raw);
  return Boolean((parsed?.rest ?? "").toLowerCase().startsWith("acp:"));
}

const THREAD_SESSION_MARKERS = [":thread:", ":topic:"];

export function resolveThreadParentSessionKey(
  sessionKey: string | undefined | null,
): string | null {
  const raw = (sessionKey ?? "").trim();
  if (!raw) {
    return null;
  }
  const normalized = raw.toLowerCase();
  let idx = -1;
  for (const marker of THREAD_SESSION_MARKERS) {
    const candidate = normalized.lastIndexOf(marker);
    if (candidate > idx) {
      idx = candidate;
    }
  }
  if (idx <= 0) {
    return null;
  }
  const parent = raw.slice(0, idx).trim();
  return parent ? parent : null;
}
